import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Pause, Play, Check, MessageSquare } from "lucide-react";
import { useFocus } from "@/contexts/FocusContext";
import { useSubtasks, useUpdateSubtask } from "@/hooks/useSubtasks";
import { useCompleteItem } from "@/hooks/useItems";
import { useAwardXP } from "@/hooks/useUserStats";
import { ItemChat } from "@/components/items/ItemChat";
import { Button } from "@/components/ui/button";
import { playFocusStart, playCycleComplete, playTick, playAbort, playTimerPulse } from "@/lib/sounds";
import { emitXpPopup } from "@/components/ui/xp-popup";

export function FocusScreen() {
  const focus = useFocus();
  const { item, phase, timerState, secondsLeft, cycleCount, isActive } = focus;
  const { data: subtasks } = useSubtasks(item?.id ?? null);
  const updateSubtask = useUpdateSubtask();
  const completeItem = useCompleteItem();
  const awardXP = useAwardXP();
  const [showChat, setShowChat] = useState(false);
  const [currentSubtaskIdx, setCurrentSubtaskIdx] = useState(0);

  // Play sounds on phase transitions
  useEffect(() => {
    if (timerState === "running" && phase === "focus" && secondsLeft === 25 * 60 - 1) {
      playFocusStart();
    }
  }, [timerState, phase, secondsLeft]);

  useEffect(() => {
    if (secondsLeft === 0 && phase !== "idle") {
      playCycleComplete();
    }
  }, [secondsLeft, phase]);

  // Pulse warning in last 60s
  useEffect(() => {
    if (phase === "focus" && secondsLeft <= 60 && secondsLeft > 0 && secondsLeft % 10 === 0) {
      playTimerPulse();
    }
  }, [secondsLeft, phase]);

  const pendingSubtasks = useMemo(
    () => subtasks?.filter((s) => s.status !== "done") ?? [],
    [subtasks]
  );

  if (!isActive || !item) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const totalDuration = phase === "focus" ? 25 * 60 : phase === "break" ? 5 * 60 : 15 * 60;
  const progress = 1 - secondsLeft / totalDuration;
  const circumference = 2 * Math.PI * 90;
  const isLastMinute = phase === "focus" && secondsLeft <= 60;

  const handleComplete = async () => {
    completeItem.mutate(
      { id: item.id, xpValue: item.xp_value, priority: item.priority },
      {
        onSuccess: async () => {
          const result = await awardXP.mutateAsync({
            itemXpValue: item.xp_value,
            itemPriority: item.priority,
            inFocusMode: true,
          });
          emitXpPopup(result.xpGained, window.innerWidth / 2, window.innerHeight / 2 - 50);
          focus.abort();
        },
      }
    );
  };

  const handleSubtaskCheck = (subtaskId: string) => {
    updateSubtask.mutate({ id: subtaskId, status: "done" });
    playTick();
    if (currentSubtaskIdx < pendingSubtasks.length - 1) {
      setCurrentSubtaskIdx((i) => i + 1);
    }
  };

  const phaseLabel = phase === "focus" ? "FOCUS" : phase === "break" ? "BREAK" : "DEEP REST";
  const ringColor = phase === "focus" ? (isLastMinute ? "text-red-500" : "text-primary") : "text-blue-400";

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-background flex flex-col"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-4" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
            <div className="flex-1 min-w-0">
              <h1 className="text-sm font-medium truncate">{item.title}</h1>
              {item.xp_value && (
                <span className="text-xs text-primary font-mono">{item.xp_value} XP</span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={() => {
                playAbort();
                focus.abort();
              }}
            >
              <X className="h-4 w-4 mr-1" />
              ABORT
            </Button>
          </div>

          {/* Center — timer ring */}
          <div className={`flex-1 flex flex-col items-center justify-center ${showChat ? "pb-0" : "pb-20"}`}>
            <div className="relative w-52 h-52">
              <svg className="w-52 h-52 -rotate-90" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="90" fill="none" stroke="currentColor" strokeWidth="4" className="text-muted/20" />
                <motion.circle
                  cx="100" cy="100" r="90" fill="none" strokeWidth="5"
                  className={ringColor}
                  stroke="currentColor"
                  strokeDasharray={circumference}
                  initial={false}
                  animate={{ strokeDashoffset: circumference * (1 - progress) }}
                  transition={{ duration: 0.5 }}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-4xl font-mono font-bold tabular-nums ${isLastMinute ? "text-red-500 animate-pulse" : ""}`}>
                  {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
                </span>
                <span className="text-xs text-muted-foreground mt-1 tracking-widest">{phaseLabel}</span>
                {cycleCount > 0 && (
                  <span className="text-[10px] text-muted-foreground mt-0.5">Cycle {cycleCount}</span>
                )}
              </div>
            </div>

            {/* Pause/Resume */}
            <div className="flex items-center gap-3 mt-6">
              {timerState === "running" ? (
                <Button variant="outline" size="sm" onClick={focus.pause}>
                  <Pause className="h-4 w-4 mr-1" /> Pause
                </Button>
              ) : timerState === "paused" ? (
                <Button variant="outline" size="sm" onClick={focus.resume}>
                  <Play className="h-4 w-4 mr-1" /> Resume
                </Button>
              ) : null}
              <Button size="sm" onClick={handleComplete}>
                <Check className="h-4 w-4 mr-1" /> MARK COMPLETE
              </Button>
            </div>

            {/* Subtask stepper */}
            {pendingSubtasks.length > 0 && (
              <div className="mt-6 w-full max-w-sm px-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">
                    Subtask {currentSubtaskIdx + 1}/{pendingSubtasks.length}
                  </span>
                  <div className="h-1 flex-1 mx-3 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${((currentSubtaskIdx) / pendingSubtasks.length) * 100}%` }}
                    />
                  </div>
                </div>
                {pendingSubtasks[currentSubtaskIdx] && (
                  <button
                    onClick={() => handleSubtaskCheck(pendingSubtasks[currentSubtaskIdx].id)}
                    className="w-full text-left px-4 py-3 rounded-lg bg-card border border-border hover:border-primary/30 transition-colors"
                  >
                    <span className="text-sm">{pendingSubtasks[currentSubtaskIdx].title}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">Tap to complete</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* AI chat toggle + panel */}
          {!showChat ? (
            <div className="px-6 pb-6" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowChat(true)}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                ASK AI
              </Button>
            </div>
          ) : (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "45%" }}
              className="border-t border-border overflow-hidden"
            >
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                  <span className="text-xs font-medium text-muted-foreground">MISSION HANDLER</span>
                  <button onClick={() => setShowChat(false)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex-1 min-h-0">
                  <ItemChat item={item} />
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
